<script setup>
import {
    eachDayOfInterval, endOfWeek, format, startOfWeek, set,
    setDefaultOptions, addDays, subDays, compareDesc, parseISO, parse
} from 'date-fns';
import {fr} from 'date-fns/locale';
import {ref, watch} from "vue";
import httpClient from "../lib/http";
import arrayRemove from "../lib/utils/arrayRemove.js";
setDefaultOptions( {locale: fr, weekStartsOn: 1} );

const parse_hour = hourStr => parse( hourStr, "HH:mm", new Date() );

const props = defineProps(['end_of_subscription_date', 'course_type'] ),
      course_type = props.course_type,
      http = httpClient(),
      courses_by_days = ref( [] ),
      selected_courses = ref( [] ),
      all_weeks = ref( false ),
      booking = ref( false ),
      show_message = ref( false ),
      loading = ref( true ),
      actual_hour = set( new Date, {} ),
      actual_date = set( new Date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 } ),
      current_date = ref( set( new Date, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 } ) ),
      end_of_subscription_date = new Date( props.end_of_subscription_date ),
      max_date_end_of_week = endOfWeek( end_of_subscription_date );

let holidays = [],
    subscription = {},
    selected_courses_dates = {},
    dirty = false,
    booked = 0,
    access = 0,
    nbr_remaining_access = 0,
    start = startOfWeek( current_date.value ),
    end = endOfWeek( current_date.value ),
    days = eachDayOfInterval( { start, end } ),
    fetch_courses_signal = null,
    fetch_booked_courses_signal = null,
    fetch_subscription_signal = null;

function selectCourse(course, date) {
    let can_add = false;
    let can_remove = false;

    if ( !selected_courses.value.includes( course.id ) ) {
        if ( !can_add && subscription.subscription_type === 'session_pass' ) {
            can_add = true;
        }

        if ( !can_add && subscription.subscription_type === 'limited_pass' && nbr_remaining_access > 0 ) {
            nbr_remaining_access = nbr_remaining_access - 1;

            can_add = true;
        }

        if ( access > booked ) {
            booked++;
            can_add = true;
        }

        if ( can_add ) {
            selected_courses.value.push( course.id );
            selected_courses_dates[ course.id ] = date;
        }
    } else if ( selected_courses.value.includes( course.id ) ) {
        if ( !can_remove && subscription.subscription_type === 'session_pass' ) {
            can_remove = true;
        }

        if ( !can_remove && subscription.subscription_type === 'limited_pass' ) {
            nbr_remaining_access = nbr_remaining_access + 1;

            can_remove = true;
        }

        if ( !can_remove ) {
            booked--;
            can_remove = true;
        }

        if ( can_remove ) {
            selected_courses.value = arrayRemove( selected_courses.value, course.id );
            delete selected_courses_dates[ course.id ];
        }
    }

    dirty = can_add || can_remove;
}

function previousWeek() {
    current_date.value = subDays( current_date.value, 7 );
}

function nextWeek() {
    current_date.value = addDays( current_date.value, 7 );
}

function actualWeek() {
    current_date.value = structuredClone( actual_date );
}

function bookCourse() {
    if ( !dirty ) {
        return;
    }

    booking.value = true;

    const booked_courses = selected_courses.value.map( course_id => ({
        course_id,
        scheduled_date: selected_courses_dates[ course_id ],
    }) );

    const data = {
        all_weeks: all_weeks.value,
        start: format( start, 'yyyy-MM-dd' ),
        end: format( end, 'yyyy-MM-dd' ),
        course_type,
        booked_courses,
    }

    http.post( '/api/v1/courses/book', data )
        .then( response => {
            dirty = false;

            booked = response.body.booked;

            if ( response.body.nbr_remaining_access ) {
                nbr_remaining_access = response.body.nbr_remaining_access;
            }
        } )
        .then( async () => await fetch() )
        .then( () => booking.value = false )
        .then( () => {
            show_message.value = true;

            setTimeout( () => show_message.value = false, 5000 );
        } );
}

async function fetchCourses() {
    try {
        if ( fetch_courses_signal ) {
            fetch_courses_signal.abort('Cancel request');
        }

        fetch_courses_signal = http.signal();

        const params = new URLSearchParams({
            start: format( start, 'yyyy-MM-dd'),
            end: format( end, 'yyyy-MM-dd'),
            course_type
        });

        let response = await http.get('/api/v1/courses/weekly?' + params.toString(), {}, {
            signal: fetch_courses_signal.signal
        });
        return courses_by_days.value = response.body;
    } catch (e) {
    }
}

async function fetchBookedCourses() {
    try {
        if ( fetch_booked_courses_signal ) {
            fetch_booked_courses_signal.abort('Cancel request');
        }

        fetch_booked_courses_signal = http.signal();

        selected_courses.value = [];
        selected_courses_dates = {};

        const params = new URLSearchParams({
            start: format( start, 'yyyy-MM-dd'),
            end: format( end, 'yyyy-MM-dd'),
            course_type
        });

        let response = await http.get('/api/v1/courses/booked?' + params.toString(), {},{
            signal: fetch_booked_courses_signal.signal,
        });
        response.body.data.forEach(course => {
            selected_courses.value.push(course.course_id);
            selected_courses_dates[course.course_id] = format(parseISO(course.scheduled_date), 'yyyy-MM-dd');
        });

        return {
            courses: selected_courses.value,
            dates: selected_courses_dates
        }
    } catch (e) {
    }
}

async function fetchSubscription() {
    try {
        if ( fetch_subscription_signal ) {
            fetch_subscription_signal.abort('Cancel request');
        }

        fetch_subscription_signal = http.signal();

        const params = new URLSearchParams({
            start: format( start, 'yyyy-MM-dd'),
            end: format( end, 'yyyy-MM-dd'),
            course_type
        });

        let response = await http.get('/api/v1/auth/subscription?' + params.toString(), {}, {
            signal: fetch_subscription_signal.signal,
        });

        subscription = response.body;
        booked = response.body.booked[course_type];
        access = response.body.access[course_type];

        if ( subscription.subscription_type === 'limited_pass' ) {
            nbr_remaining_access = response.body.nbr_remaining_access;
        }

        return {
            subscription,
            booked,
            access,
            nbr_remaining_access
        };
    } catch (e) {
    }
}

async function fetch() {
    loading.value = true;

    await fetchCourses();
    await fetchBookedCourses();
    await fetchSubscription();

    loading.value = false;
}

watch( current_date, async new_date => {
    loading.value = true;
    dirty = false;

    start = startOfWeek( new_date );
    end = endOfWeek( new_date );
    days = eachDayOfInterval( { start, end } );

    await fetch();
} );

http.get( '/api/v1/courses/holidays' )
    .then( response => holidays = response.body.map( date => date.slice(0, 10) ) )
    .then( async () => await fetch() );

</script>

<template>
    <div class="w-full">
        <div class="flex flex-col md:flex-row mt-4 mb-10">
            <div class="text-white text-sm md:w-1/2 mb-10 md:mb-0">
                Semaine du <span class="font-bold border-b">{{ format( start, 'dd LLLL yyyy' ) }}</span> au <span class="font-bold border-b">{{ format( end, 'dd LLLL yyyy' ) }}</span>
            </div>

            <div class="flex flex-row md:w-1/2 md:justify-end">
                <button class="px-1.5 py-1 mr-5 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors"
                        :class="{
                            'opacity-10 pointer-events-none': compareDesc( current_date, actual_date ) === 0,
                        }" @click="actualWeek" type="button">
                    <i class="fa-solid fa-fw fa-calendar-week mr-0.5"></i> Cette semaine
                </button>

                <button class="px-1.5 py-1 mr-2.5 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors ml-auto md:ml-0"
                    :class="{
                        'opacity-10 pointer-events-none': compareDesc( current_date, actual_date ) >= 0,
                    }" @click="previousWeek">
                    <i class="fa-solid fa-fw fa-chevron-left"></i>
                </button>

                <button class="px-1.5 py-1 text-sm text-white rounded-md border border-white/10 hover:bg-orange transition-colors"
                        :class="{
                            'opacity-10 pointer-events-none': compareDesc( max_date_end_of_week, end ) >= 0,
                        }" @click="nextWeek">
                    <i class="fa-solid fa-fw fa-chevron-right"></i>
                </button>
            </div>
        </div>

        <div class="w-full flex flex-col md:flex-row">
            <div class="mb-8 md:w-1/2" v-if="subscription.subscription_type !== 'session_pass'">
                <label class="text-white cursor-pointer">
                    <input type="checkbox" name="all_weeks" id="input_all_weeks" class="relative top-1 w-5 h-5 mr-2" v-model="all_weeks">
                    <span class="xl:inline-block">Réserver ces cours pour les prochaines semaines jusqu'au <span class="border-b">{{ format( max_date_end_of_week, 'dd LLLL yyyy' ) }}</span></span>
                </label>
            </div>

            <p class="text-white md:w-1/2 mb-5" v-if="subscription.subscription_type === 'session_pass'">
                <i class="fa-solid fa-circle-info fa-fw"></i> <span>Je m'engage à régler la somme de <span class="font-bold border-b">15 &euro;</span> en arrivant avant chaque séance réservée</span>
            </p>

            <div class="flex flex-col md:w-1/2 mb-5">
                <p class="text-white md:text-right" :class="{'pointer-events-none opacity-25': loading}"
                   v-if="['special_pass', 'fitness_pass'].includes( subscription.subscription_type )">
                    <strong>À réserver :</strong>
                    <br/>
                    <span class="border-b">{{ booked }} / {{ access }}</span> séance{{ access > 1 ? 's' : '' }} de
                    <template v-if="course_type === 'dance'">danse</template>
                    <template v-if="course_type === 'fitness'">fitness</template>
                    <br/>
                    <span class="border-b">{{ subscription.nbr_cancellations }} / 3</span> annulations dans les 24h
                </p>

                <p class="text-white md:text-right mb-2" v-if="subscription.subscription_type === 'limited_pass'">
                    <span class="font-bold border-b">{{ nbr_remaining_access }}</span> séance{{ nbr_remaining_access > 1 ? 's' : '' }} jusqu'à la fin de la validité de votre carte
                </p>

                <p class="text-white md:text-right mb-5" :class="{'pointer-events-none opacity-25': loading}"
                   v-if="['limited_pass', 'session_pass'].includes( subscription.subscription_type )">
                    <span class="border-b">{{ subscription.nbr_cancellations }} / 3</span> annulations dans les 24h
                </p>
            </div>
        </div>

        <div class="flex flex-col lg:flex-row w-full gap-x-2 mb-10" :class="{'pointer-events-none opacity-25': loading}">
            <div v-for="(date, i) in days" :key="i"
                 class="flex flex-col w-full"
                 :class="{
                    'hidden': courses_by_days[ i ] === undefined
                 }"
            >
                <div class="bg-purple border-b-2 text-center text-white content-center uppercase p-4 mb-5">
                    <span>{{ format( date, 'eeee' ) }}</span>
                    <br/>
                    <span class="text-xs">{{ format( date, 'dd LLLL' ) }}</span>
                </div>

                <div class="text-white text-center py-6 px-2 2xl:px-4 mb-2 transition-colors border border-transparent"
                    v-if="holidays.includes( format( date, 'yyyy-MM-dd') )">
                    <p class="font-bold uppercase">Cours non programmé</p>
                </div>

                <div v-if="!holidays.includes( format( date, 'yyyy-MM-dd') )"
                    v-for="(course, index) in courses_by_days[ i ]" :key="index" @click="() => selectCourse( course, format( date, 'yyyy-MM-dd') )"
                    class="text-white text-center p-2 2xl:px-4 mb-2 cursor-pointer transition-colors border"
                    :class="{
                        'bg-dance/50 border-white/70': selected_courses.includes( course.id ) && course.course_type === 'dance',
                        'bg-fitness/50 border-white/70': selected_courses.includes( course.id ) && course.course_type === 'fitness',
                        'bg-white/10 border-transparent hover:border-white': !selected_courses.includes( course.id ),
                        'opacity-10 pointer-events-none':
                            actual_date > date || actual_date >= date && actual_hour >= parse_hour( course.schedule_end ) ||
                            date > end_of_subscription_date,
                    }"
                    >
                    <span class="text-xs">{{ course.schedule_start }} - {{ course.schedule_end }}</span>
                    <p class="font-bold uppercase">{{ course.title }}</p>
                    <p class="text-xs" v-if="course.nbr_places > 0">{{ course.nbr_places - course.count_places }} places</p>
                </div>
            </div>
        </div>

        <div class="text-center flex flex-col mb-8">
            <div :class="{'pointer-events-none opacity-25': loading}">
                <button class="button | p-5" @click="bookCourse" :disabled="booking || !dirty">
                    <span v-if="!loading">Réserver <i class="ml-2 fa-solid fa-check" v-if="!( booking || !dirty )"></i></span>
                    <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
                </button>
            </div>

            <transition>
                <div class="mt-5 text-green-500" v-if="show_message">
                    <i class="fa-solid fa-circle-check mr-2 text-xl"></i> Vos réservations ont bien été mises à jour !
                </div>
            </transition>
        </div>
    </div>
</template>